import React, { JSX, useEffect, useState } from 'react'
import classNames from 'classnames'
import { TriviaSummary } from 'types/api'

export const DEFAULT_TRIVIA_GRACE_SECONDS = 5

export type TriviaPhase = 'draft' | 'live' | 'grace' | 'locked' | 'revealed'

export const triviaPhaseOf = (trivia: TriviaSummary, nowMs: number): TriviaPhase => {
  if (trivia.revealedAt != null) return 'revealed'
  if (trivia.closedAt != null) {
    const graceEnd = trivia.graceEndsAt != null ? Date.parse(trivia.graceEndsAt) : Number.NaN
    return Number.isFinite(graceEnd) && nowMs < graceEnd ? 'grace' : 'locked'
  }
  return trivia.status === 'ACTIVE' ? 'live' : 'draft'
}

export const triviaRevealArmed = (trivia: TriviaSummary, nowMs: number): boolean => {
  if (trivia.revealedAt != null) return false
  if (trivia.closedAt != null) return triviaPhaseOf(trivia, nowMs) === 'locked'
  return trivia.status === 'INACTIVE' && trivia.startedAt != null && trivia.stoppedAt != null
}

export const TRIVIA_PHASE_STYLES: Record<TriviaPhase, { label: string, text: string, dot: string }> = {
  draft: { label: 'Draft', text: 'text-zinc-400', dot: 'bg-zinc-400' },
  live: { label: 'Live', text: 'text-emerald-300', dot: 'bg-emerald-400' },
  grace: { label: 'Grace', text: 'text-amber-300', dot: 'bg-amber-400' },
  locked: { label: 'Locked', text: 'text-sky-300', dot: 'bg-sky-400' },
  revealed: { label: 'Revealed', text: 'text-fuchsia-300', dot: 'bg-fuchsia-400' }
}

export const triviaResetConfirmCopy = (label: string, revealed: boolean): string => {
  if (revealed) {
    return `Reset "${label}"?\n\n` +
      'This question is revealed. WARNING: resetting permanently DELETES every recorded answer for it, sets all counts back to zero and removes it from the leaderboard. It cannot be undone.\n\n' +
      'The question goes back to a startable draft with the same correct answer. To keep this round\'s results on the leaderboard, do not reset: duplicate the question and run the copy instead.\n\n' +
      'Delete all answers and reset?'
  }
  return `Reset "${label}"?\n\n` +
    'WARNING: this permanently DELETES every recorded answer for this question and sets all counts back to zero. It cannot be undone.\n\n' +
    'Delete all answers and reset?'
}

export const TriviaStatusChip = ({ phase }: { phase: TriviaPhase }): JSX.Element => {
  const style = TRIVIA_PHASE_STYLES[phase]
  return (
    <span className={classNames('inline-flex items-center gap-1.5 text-[11px] font-medium', style.text)}>
      <span className={classNames('h-1.5 w-1.5 shrink-0 rounded-full', style.dot)} />
      {style.label}
    </span>
  )
}

export const useTriviaGraceTick = (trivias: TriviaSummary[]): number => {
  const [nowMs, setNowMs] = useState<number>(() => Date.now())
  const anyGrace = trivias.some((t) =>
    t.revealedAt == null &&
    t.closedAt != null &&
    t.graceEndsAt != null &&
    Date.parse(t.graceEndsAt) > nowMs
  )
  useEffect(() => {
    if (!anyGrace) return
    const id = globalThis.setInterval(() => setNowMs(Date.now()), 1000)
    return () => globalThis.clearInterval(id)
  }, [anyGrace])
  return nowMs
}
